import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Autocomplete,
  Typography,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { printPurchasePaymentThermal } from "../utils/printPurchasePaymentThermal";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const PurchasePaymentAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd(
    "shop/purchase-payments",
    token
  );

  const { data: shopsData, isLoading: shopLoading } = useFetchObjects(
    ["shop", "purchase-payment-add"],
    "shop/shop",
    token
  );

  const [selectedShop, setSelectedShop] = useState("");
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "purchase-payment-add", selectedShop],
    selectedShop ? `shop/currencies/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: purchasesData, loading: purchaseLoading } = useFetchObjects(
    ["purchase", "purchase-payment-add", selectedShop],
    selectedShop ? `shop/purchases/?page_size=all&shop=${selectedShop}` : "",
    token
  );

  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData?.results)
      ? currenciesData.results
      : Array.isArray(currenciesData)
      ? currenciesData
      : []
  );
  const purchases = Array.isArray(purchasesData?.results) ? purchasesData.results : (Array.isArray(purchasesData) ? purchasesData : []);

  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [currencyDetails, setCurrencyDetails] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remainedAmount, setRemainedAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  ); // Format for datetime-local
  const [notes, setNotes] = useState("");

  const selectedShopId = selectedShop || selectedPurchase?.shop_details?.id || "";
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "purchase-payment-add", selectedShopId],
    selectedShopId ? `shop/accounts/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const shopAccounts = accounts;
  const sameCurrencyAccounts = accounts.filter(
    (acc) => String(acc.currency) === String(selectedCurrency)
  );

  const { data: directRateData } = useFetchObjects(
    [
      "purchase-payment-rate-direct",
      selectedShopId,
      currencyDetails?.id,
      selectedAccount?.currency,
    ],
    selectedShopId && currencyDetails?.id && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${currencyDetails.id}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );

  const { data: reverseRateData } = useFetchObjects(
    [
      "purchase-payment-rate-reverse",
      selectedShopId,
      currencyDetails?.id,
      selectedAccount?.currency,
    ],
    selectedShopId && currencyDetails?.id && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${currencyDetails.id}`
      : "",
    token
  );

  // Handle purchase selection
  const handlePurchaseChange = (event, value) => {
    setSelectedPurchase(value);
    if (value) {
      const currency = value.currency_details;
      setCurrencyDetails(currency);
      setSelectedCurrency(currency?.id ? String(currency.id) : "");
      setTotalAmount(value.total_price[currency.title] || 0);
      setPaidAmount(value.total_paid[currency.title] || 0);
      setRemainedAmount(value.total_remained_amount[currency.title] || 0);
      setSelectedAccountId("");
      setSelectedAccount(null);
    } else {
      setCurrencyDetails(null);
      setSelectedCurrency("");
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
      setSelectedAccountId("");
      setSelectedAccount(null);
    }
  };

  const handleShopChange = (e) => {
    const shopId = e.target.value;
    setSelectedShop(shopId);
    setSelectedPurchase(null);
    setSelectedCurrency("");
    setCurrencyDetails(null);
    setTotalAmount(0);
    setPaidAmount(0);
    setRemainedAmount(0);
    setAmount("");
    setSelectedAccountId("");
    setSelectedAccount(null);
  };
  useEffect(() => {
    if (!selectedAccountId) {
      setSelectedAccount(null);
      return;
    }
    const acc = accounts.find((a) => String(a.id) === String(selectedAccountId));
    setSelectedAccount(acc || null);
  }, [selectedAccountId, accounts]);

  useEffect(() => {
    if (!selectedCurrency) {
      setSelectedAccountId("");
      return;
    }
    if (shopAccounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = shopAccounts.some(
      (acc) => String(acc.id) === String(selectedAccountId)
    );
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || shopAccounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, selectedAccountId, shopAccounts, sameCurrencyAccounts]);

  const exchangeRatePreview = (() => {
    if (!currencyDetails) return "";
    const accountCurrencyId = selectedAccount?.currency ?? currencyDetails.id;
    if (String(currencyDetails.id) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  })();

  const convertedAmountPreview = (() => {
    if (!amount || !exchangeRate) return "";
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  })();

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  // Handle amount validation
  const handleAmountChange = (e) => {
    const value = e.target.value;
    const numericValue = Number(value);
    if (value === "" || numericValue <= remainedAmount) {
      setAmount(value);
    } else {
      console.error("Amount cannot exceed the remaining amount.");
    }
  };

  const handleSubmit = () => {
    if (!selectedPurchase || !amount || amount > remainedAmount) {
      console.error("Validation failed.");
      return;
    }

    const formData = new FormData();
    formData.append("purchase", selectedPurchase.id);
    formData.append("currency", selectedCurrency);
    if (selectedAccountId) formData.append("account", selectedAccountId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    formData.append("payment_date", paymentDate); // Submit payment date
    formData.append("notes", notes);
    handleAdd(formData);
  };

  // Reset form after successful addition
  useEffect(() => {
    if (resetForm) {
      setSelectedPurchase(null);
      setSelectedShop("");
      setSelectedCurrency("");
      setAmount("");
      setExchangeRate("");
      setCurrencyDetails(null);
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
      setPaymentDate(new Date().toISOString().slice(0, 16)); // Reset payment date
      setNotes("");
      setSelectedAccountId("");
      setSelectedAccount(null);
      onClose();
    }
  }, [resetForm, onClose]);

  // Safeguard against undefined data
  const purchaseList = purchases || [];
  const currencyList = currencies || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("purchasePayment.addPayment")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {/* Purchase Selection with Autocomplete */}
          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectShop", "Select branch")}
            fullWidth
            required
            value={selectedShop}
            onChange={handleShopChange}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <Autocomplete
            options={selectedShop ? purchaseList : []}
            getOptionLabel={(option) =>
              `Purchase #${option.id} - ${option.supplier_details?.name || 'N/A'} - ${option.shop_details?.name || 'N/A'}`
            }
            value={selectedPurchase}
            onChange={handlePurchaseChange}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t("purchasePayment.selectPurchase")}
                fullWidth
                required
              />
            )}
            disabled={!selectedShop}
            loading={purchaseLoading}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography>
                  <strong>Purchase #{option.id}</strong> - {option.supplier_details?.name || 'N/A'} - {option.shop_details?.name || 'N/A'}
                </Typography>
              </Box>
            )}
          />

          {/* Display Total, Paid, and Remaining Amounts */}
          {selectedPurchase && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Total:</strong> {totalAmount.toFixed(2)} {currencyDetails?.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Paid:</strong> {paidAmount.toFixed(2)} {currencyDetails?.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                <strong>Remaining:</strong> {remainedAmount.toFixed(2)} {currencyDetails?.title}
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 1 }}
                onClick={() => printPurchasePaymentThermal({
                  totalAmount: totalAmount,
                  requestedAmount: amount || 0,
                  remainingAmount: remainedAmount,
                  currencySymbol: currencyDetails?.title,
                  supplierName: selectedPurchase?.supplier_details?.name
                })}
                disabled={!amount}
              >
                Print Receipt
              </Button>
            </Box>
          )}

          {/* Currency Selection (Disabled, as it's derived from the purchase) */}
          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            disabled={!selectedShop || currencyLoading}
            onChange={(e) => {
              setSelectedCurrency(e.target.value);
              const selected = currencyList.find(
                (c) => String(c.id) === String(e.target.value)
              );
              setCurrencyDetails(selected || null);
              if (selectedPurchase && selected) {
                setTotalAmount(selectedPurchase.total_price?.[selected.title] || 0);
                setPaidAmount(selectedPurchase.total_paid?.[selected.title] || 0);
                setRemainedAmount(
                  selectedPurchase.total_remained_amount?.[selected.title] || 0
                );
              }
            }}
          >
            {!selectedShop ? (
              <MenuItem value="" disabled>
                {t("purchasePayment.selectShopFirst", "Select branch first")}
              </MenuItem>
            ) : currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              currencyList.map((curr) => (
                <MenuItem key={curr.id} value={String(curr.id)}>
                  {curr.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.account", "Account")}
            fullWidth
            required
            disabled={!selectedShopId || accountLoading}
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("purchasePayment.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("purchasePayment.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={handleAmountChange}
            inputProps={{
              max: remainedAmount,
            }}
            error={amount > remainedAmount}
            helperText={
              amount > remainedAmount
                ? "Amount cannot exceed the remaining amount."
                : ""
            }
          />

          <TextField
            margin="dense"
            label={t("purchasePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          {!exchangeRatePreview && selectedPurchase && selectedAccount && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("purchasePayment.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}

          <TextField
            margin="dense"
            label={t("purchasePayment.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmountPreview}
            disabled
          />

          {/* Payment Date Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.paymentDate")}
            type="datetime-local" // Using datetime-local input
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} // Handle date change
          />

          <TextField
            margin="dense"
            label={t("purchasePayment.notes")}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchasePayment.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedPurchase ||
            !selectedCurrency ||
            !amount ||
            !exchangeRate
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchasePayment.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePaymentAdd;
